import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { Gql } from '@fiverr-private/dynamic_matching';
import { useBriefCreationContext } from '../../context/BriefCreationContext';
import { mixpanelEvents, reportTimeMetric } from '../../monitoring';
import { retryWrapper } from '../../utils';
import { getBuyerData } from '../../api/buyerData';
import { handleGetBuyerDataError } from './utils';
import { QUERY_RETRY_DELAY_MS } from './constants';

const requestType = 'get_buyer_data';

export const usePopulateBuyerType = () => {
    const queryClient = useQueryClient();
    const { setBuyerType, variant } = useBriefCreationContext();
    const [isBuyerTypeLoading, setIsBuyerTypeLoading] = useState(false);

    const populateBuyerType = async (abortController: AbortController): Promise<Gql.BuyerType | undefined> => {
        const getBuyerDataStartTime = Date.now();

        return retryWrapper<Gql.BuyerType | undefined>({
            retries: 2,
            metric: requestType,
            wrappedFn: async () => {
                try {
                    setIsBuyerTypeLoading(true);
                    const { data } = await queryClient.fetchQuery({
                        queryKey: [requestType],
                        queryFn: () => getBuyerData(abortController),
                        cacheTime: 0,
                        retry: 2,
                        retryDelay: QUERY_RETRY_DELAY_MS,
                    });

                    if (!data.insights) {
                        handleGetBuyerDataError({
                            requestType,
                            errorMessage: 'Failed to GET: get_dynamic_matching_buyer_data',
                        });
                        setBuyerType(undefined);
                        return undefined;
                    }

                    const buyerType = data?.insights?.state?.type;
                    if (buyerType) {
                        setBuyerType(buyerType);
                    } else {
                        setBuyerType(undefined);
                    }

                    return buyerType;
                } catch (error: any) {
                    mixpanelEvents.seamless(variant).generateBrief.submit('Failed');
                    handleGetBuyerDataError({
                        requestType,
                        errorMessage: error.message,
                        isTimeout: error.message === 'No data',
                        isAborted: abortController.signal.aborted,
                    });
                    throw error;
                } finally {
                    setIsBuyerTypeLoading(false);
                    reportTimeMetric({
                        metricName: requestType,
                        time: Date.now() - getBuyerDataStartTime,
                        timeout: QUERY_RETRY_DELAY_MS,
                    });
                }
            },
        });
    };

    return {
        populateBuyerType,
        isBuyerTypeLoading,
    };
};
